import {
  Component,
  signal,
  computed,
  HostListener,
  OnInit,
  inject,
  OnDestroy,
  PLATFORM_ID,
  LOCALE_ID,
  DestroyRef,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { RouterLink, Router } from '@angular/router';
import { SafeUrl } from '@angular/platform-browser';
import { trigger, transition, style, animate, query, group } from '@angular/animations';
import { finalize } from 'rxjs';
import { MFGCompanyService } from '../../../../services/product/company.service';
import { CompanyLookupItem } from '../../../../services/product/company.model';
import { ProductsFileMgt } from '../../../../services/product/file-management/products.file-mgt.service';
import { ArrowNavIcon } from "../../../../shared/ui/icons/arrow-nav-icon/arrow-nav-icon";

export interface Company {
  id: number;
  titleEn: string;
  titleAr: string;
}

@Component({
  selector: 'app-companies-section',
  standalone: true,
  imports: [RouterLink, ArrowNavIcon],
  templateUrl: './companies-section.html',
  styleUrl: './companies-section.scss',
  animations: [
    trigger('swapAnimation', [
      transition(':increment', [
        group([
          query(
            ':enter',
            [
              style({ transform: 'translateX(100%)', opacity: 0 }),
              animate('1000ms cubic-bezier(0.4, 0, 0.2, 1)', style({ transform: 'translateX(0)', opacity: 1 })),
            ],
            { optional: true },
          ),
          query(
            ':leave',
            [
              style({ position: 'absolute', width: 'calc(100% / var(--items-per-view, 4))' }),
              animate('1000ms cubic-bezier(0.4, 0, 0.2, 1)', style({ transform: 'translateX(-100%)', opacity: 0 })),
            ],
            { optional: true },
          ),
        ]),
      ]),
      transition(':decrement', [
        group([
          query(
            ':enter',
            [
              style({ transform: 'translateX(-100%)', opacity: 0 }),
              animate('1000ms cubic-bezier(0.4, 0, 0.2, 1)', style({ transform: 'translateX(0)', opacity: 1 })),
            ],
            { optional: true },
          ),
          query(
            ':leave',
            [
              style({ position: 'absolute', width: 'calc(100% / var(--items-per-view, 4))' }),
              animate('1000ms cubic-bezier(0.4, 0, 0.2, 1)', style({ transform: 'translateX(100%)', opacity: 0 })),
            ],
            { optional: true },
          ),
        ]),
      ]),
    ]),
  ],
})
export class CompaniesSection implements OnInit, OnDestroy {
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);
  private locale = inject(LOCALE_ID);
  private companyService = inject(MFGCompanyService);
  private fileMgt = inject(ProductsFileMgt);
  private destroyRef = inject(DestroyRef);
  private autoplayInterval: any;

  isArabic = computed(() => this.locale.startsWith('ar'));

  loading = signal<boolean>(false);
  error = signal<string>('');
  companies = signal<Company[]>([]);
  companyImages = new Map<number, { url: SafeUrl | string; isImage: boolean }>();

  currentIndex = signal(0);
  itemsPerView = signal(4);

  ngOnInit() {
    this.updateItemsPerView();
    this.getCompanies();
    if (isPlatformBrowser(this.platformId)) {
      this.startAutoplay();
    }
  }

  getCompanies() {
    this.loading.set(true);
    this.error.set('');
    const subscription = this.companyService
      .getAllCompaniesLookup()
      .pipe(
        finalize(() => {
          this.loading.set(false);
        }),
      )
      .subscribe({
        next: (res) => {
          this.companies.set(res.list.map((c) => this.toCompany(c)));
          if (isPlatformBrowser(this.platformId)) {
            this.loadCompanyImages(res.list);
          }
        },
        error: () => {
          this.error.set(this.isArabic() ? 'حدث خطأ أثناء تحميل الشركات' : 'Failed to load companies');
        },
      });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  private loadCompanyImages(list: CompanyLookupItem[]) {
    list.forEach((company: CompanyLookupItem) => {
      if (company.profileImageFid) {
        this.fileMgt.getImageUrl(company.profileImageFid).subscribe({
          next: (imgRes) => {
            this.companyImages.set(company.id, imgRes);
          },
          error: () => {
            this.companyImages.set(company.id, { url: '', isImage: false });
          },
        });
      }
    });
  }

  private toCompany(item: CompanyLookupItem): Company {
    return {
      id: item.id,
      titleEn: item.titleEn,
      titleAr: item.titleAr,
    };
  }

  startAutoplay() {
    this.autoplayInterval = setInterval(() => {
      this.scrollRight();
    }, 4000);
  }

  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
    }
  }

  @HostListener('window:resize')
  updateItemsPerView() {
    const width = window.innerWidth;
    if (width >= 1536) this.itemsPerView.set(6);
    else if (width >= 1280) this.itemsPerView.set(5);
    else if (width >= 1024) this.itemsPerView.set(4);
    else if (width >= 768) this.itemsPerView.set(3);
    else if (width >= 640) this.itemsPerView.set(2);
    else this.itemsPerView.set(1);
  }

  visibleCompanies = computed(() => {
    const companies = this.companies();
    if (!companies.length) return [];
    const start = this.currentIndex();
    const count = this.itemsPerView();
    const result: Company[] = [];
    for (let i = 0; i < count; i++) {
      const index = (start + i) % companies.length;
      result.push(companies[index]);
    }
    return result;
  });

  scrollLeft() {
    if (!this.companies().length) return;
    this.stopAutoplay();
    const total = this.companies().length;
    this.currentIndex.update((current) => (current - 1 + total) % total);
    this.startAutoplay();
  }

  scrollRight() {
    if (!this.companies().length) return;
    const total = this.companies().length;
    this.currentIndex.update((current) => (current + 1) % total);
  }

  viewCompany(company: Company) {
    this.router.navigate(['/user/products'], {
      queryParams: {
        mfgCompanyId: company.id,
        mfgCompanyTitleEn: company.titleEn,
        mfgCompanyTitleAr: company.titleAr,
      },
    });
  }

  ngOnDestroy() {
    this.stopAutoplay();
  }
}
